import React from 'react';

import { ITEMS, ItemType } from './constants';
import { useUIState, useMapState } from './contexts/GameStateManager';
import DebugMenu from './DebugMenu';
import { getTileName } from './GameBoard';
import { DebugState } from './hooks/useDebugState';
import { useMusicSettings } from './hooks/useMusicSettings';
import { useSoundSettings } from './hooks/useSoundSettings';

// Tile info interface
interface TileInfo {
  name: string;
  character: string | undefined;
  position: string;
}

// LeftPanel props interface - simplified to only what can't come from context
interface LeftPanelProps {
  debugState: DebugState;
  setDebugState: React.Dispatch<React.SetStateAction<DebugState>>;
}

const ITEM_NAME_BY_TYPE: Record<ItemType, string> = Object.values(ITEMS).reduce(
  (acc, item) => {
    acc[item.type] = item.name;
    return acc;
  },
  {} as Record<ItemType, string>
);

const LeftPanel: React.FC<LeftPanelProps> = ({ debugState, setDebugState }) => {
  // Use contexts for state management
  const uiState = useUIState();
  const mapState = useMapState();
  const { soundSettings, setSoundSettings, resetSoundSettings } =
    useSoundSettings();
  const { musicSettings, setMusicSettings } = useMusicSettings();

  // Destructure values from contexts
  const {
    leftPanelOpen: isOpen,
    setLeftPanelOpen: setIsOpen,
    buildMode,
    selectedBuilding,
    debugMenuOpen,
    setDebugMenuOpen,
  } = uiState;

  const {
    playerFacing,
    getTargetPosition,
    getTile,
    gameMap,
    buildingLayer,
    addItemToInventory,
    prestigePoints,
    prestigeCount,
    hasEnabledDebug,
    setHasEnabledDebug,
    resetResearch,
    resetAllResearch,
    forcePrestige,
  } = mapState;

  // Handler for enabling debug mode
  const handleEnableDebugMode = (): void => {
    setHasEnabledDebug(true);
  };

  if (!isOpen) return null;

  // Get information about the tile the player is facing
  const getFacingTileInfo = (): TileInfo => {
    const targetPos = getTargetPosition(selectedBuilding || undefined);
    if (!targetPos || !getTile)
      return { name: 'Unknown', character: '?', position: '(?, ?)' };

    const facingTile = getTile(
      targetPos.x,
      targetPos.y,
      gameMap,
      buildingLayer
    );
    return {
      name: getTileName(facingTile),
      character: facingTile,
      position: `(${targetPos.x}, ${targetPos.y})`,
    };
  };

  const buildModeItemCostText =
    selectedBuilding?.cost.items && selectedBuilding.cost.items.length > 0
      ? selectedBuilding.cost.items
          .map(requirement => {
            const itemName =
              ITEM_NAME_BY_TYPE[requirement.item] ?? requirement.item;
            return `${requirement.amount} ${itemName}`;
          })
          .join(', ')
      : null;

  const activeCheats: string[] = [];
  if (debugState.collisionDisabled) {
    activeCheats.push('No Collision');
  }
  if (debugState.speedBoost) {
    activeCheats.push('Speed Boost');
  }
  if (debugState.ignoreBuildingCosts) {
    activeCheats.push('Free Building Placement');
  }
  if (debugState.ignorePowerCosts) {
    activeCheats.push('Unlimited Power');
  }

  return (
    <div className='left-panel'>
      <div className='panel-header'>
        <h3>Game Settings</h3>
        <button
          className='close-button-header'
          onClick={() => setIsOpen(false)}
        >
          ✕
        </button>
      </div>

      <div className='panel-content'>
        {/* Game Status */}
        <div className='info-section'>
          <h4>Status</h4>
          <p>Facing: {playerFacing}</p>
          <p>Looking at: {getFacingTileInfo().name}</p>
          <p>
            Tile: {getFacingTileInfo().character} at{' '}
            {getFacingTileInfo().position}
          </p>
          <p>Prestige Points: {prestigePoints}</p>
          <p>Total Prestiges: {prestigeCount}</p>
        </div>

        {/* Build Mode Status */}
        {buildMode && selectedBuilding && (
          <div className='info-section'>
            <h4>Build Mode</h4>
            <p>Selected: {selectedBuilding.name}</p>
            <p>Character: {selectedBuilding.character}</p>
            <p>Cost: {buildModeItemCostText ?? 'None'}</p>
            <p>
              Press <kbd>SPACE</kbd> to place
            </p>
          </div>
        )}

        <div className='info-section'>
          <h4>Audio Settings</h4>
          <label className='debug-checkbox'>
            <input
              type='checkbox'
              checked={soundSettings.enabled}
              onChange={() =>
                setSoundSettings(prev => ({
                  ...prev,
                  enabled: !prev.enabled,
                }))
              }
            />
            Enable building sound effects
          </label>

          <div className='resource-input'>
            <label htmlFor='overall-volume'>Overall Volume</label>
            <div className='sound-volume-control'>
              <input
                id='overall-volume'
                type='range'
                min='0'
                max='100'
                step='5'
                value={Math.round(soundSettings.masterVolume * 100)}
                onChange={event => {
                  const nextValue = Number.parseInt(event.target.value, 10);
                  if (Number.isNaN(nextValue)) {
                    return;
                  }
                  setSoundSettings(prev => ({
                    ...prev,
                    masterVolume: nextValue / 100,
                  }));
                }}
              />
              <span className='sound-volume-value'>
                {Math.round(soundSettings.masterVolume * 100)}%
              </span>
            </div>
          </div>

          <div className='resource-input'>
            <label htmlFor='sfx-volume'>SFX Volume</label>
            <div className='sound-volume-control'>
              <input
                id='sfx-volume'
                type='range'
                min='0'
                max='100'
                step='5'
                value={Math.round(soundSettings.effectsVolume * 100)}
                onChange={event => {
                  const nextValue = Number.parseInt(event.target.value, 10);
                  if (Number.isNaN(nextValue)) {
                    return;
                  }
                  setSoundSettings(prev => ({
                    ...prev,
                    effectsVolume: nextValue / 100,
                  }));
                }}
              />
              <span className='sound-volume-value'>
                {Math.round(soundSettings.effectsVolume * 100)}%
              </span>
            </div>
          </div>

          <div className='resource-input'>
            <label htmlFor='music-volume'>Music Volume</label>
            <div className='sound-volume-control'>
              <input
                id='music-volume'
                type='range'
                min='0'
                max='100'
                step='5'
                value={Math.round(musicSettings.volume * 100)}
                onChange={event => {
                  const nextValue = Number.parseInt(event.target.value, 10);
                  if (Number.isNaN(nextValue)) {
                    return;
                  }

                  setMusicSettings(prev => ({
                    ...prev,
                    volume: nextValue / 100,
                  }));
                }}
              />
              <span className='sound-volume-value'>
                {Math.round(musicSettings.volume * 100)}%
              </span>
            </div>
          </div>

          <p>
            Use the sliders to fine-tune the overall, sound effect, and music
            volumes.
          </p>
        </div>

        {/* Debug Options */}
        <div className='info-section'>
          <h4>Debug</h4>
          <button
            onClick={() => setDebugMenuOpen(!debugMenuOpen)}
            className={`debug-toggle ${debugMenuOpen ? 'active' : ''}`}
          >
            {debugMenuOpen ? 'Close Debug Menu' : 'Open Debug Menu'}
          </button>

          {activeCheats.length > 0 && (
            <div className='debug-status'>
              <p>Active Cheats:</p>
              {activeCheats.map(cheat => (
                <p key={cheat}>• {cheat}</p>
              ))}
            </div>
          )}
        </div>

        {/* Controls Reminder */}
        <div className='info-section'>
          <h4>Quick Controls</h4>
          <p>
            <kbd>B</kbd> - Build menu
          </p>
          <p>
            <kbd>ESC</kbd> - Close menus
          </p>
          <p>
            <kbd>F10</kbd> - Debug menu
          </p>
          <p>
            <kbd>F5</kbd> - Quick save
          </p>
        </div>
      </div>

      {/* Debug Menu - render inline within panel */}
      {debugMenuOpen && (
        <div className='debug-panel-content'>
          <DebugMenu
            debugMenuOpen={debugMenuOpen}
            setDebugMenuOpen={setDebugMenuOpen}
            gameState={{
              addItems: addItemToInventory,
              hasEnabledDebug,
              resetResearch,
              resetAllResearch,
              prestigePoints,
              prestigeCount,
              forcePrestige,
            }}
            debugState={debugState}
            setDebugState={setDebugState}
            onEnableDebugMode={handleEnableDebugMode}
            soundSettings={soundSettings}
            setSoundSettings={setSoundSettings}
            resetSoundSettings={resetSoundSettings}
          />
        </div>
      )}
    </div>
  );
};

export default LeftPanel;
